import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult } from '../voyage/schemas.js';
import { pdiLevelOf } from './library.js';
import {
  optionalDay,
  optionalParagraph,
  optionalText,
  optionalUuid,
  parsed,
  requiredParagraph,
  requiredText,
  typed,
} from './schema.js';

/**
 * Pre-Docking bulgusu, dry dock işi, bağlar ve ofis kararı (ADR 0129 §6, §7, Ek Faz 3; Excel "V1.2
 * Form Field Schema" Finding / Job / Link, "Entity Relationships", "Software Workflow" 6–9, 11).
 * Saf modül.
 *
 * - Bulgu gözlemdir, iş onaylı iş kapsamıdır, arıza doğrulanmış ekipman eksikliğidir; bağımsız
 *   kayıtlardır ve hiçbiri kendiliğinden açılmaz (R2, R4).
 * - Bağlar çoktan çoğadır (R3) ve değiştirilemez; iptali gerekçelidir.
 * - Onay içerik hash'ini taşır: işin kopyası (alanlar + kaynak bağları) değişince iş yeniden onaya
 *   düşer (CLAUDE.md "Onay"). Durum saklanmaz, gönderim ve karar satırlarından türetilir.
 */

export const PDI_FINDING_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type PdiFindingPriority = (typeof PDI_FINDING_PRIORITIES)[number];

/** Önerilen karar (Excel "Proposed Disposition"). */
export const PDI_DISPOSITIONS = ['monitor', 'repair', 'dry_dock', 'survey', 'no_action'] as const;
export type PdiDisposition = (typeof PDI_DISPOSITIONS)[number];

/** Ticaret (Excel "Hull, mechanical, electrical, coating etc."); boş = TBD. */
export const DRYDOCK_TRADES = [
  'hull',
  'structural',
  'mechanical',
  'electrical',
  'piping',
  'coating',
  'outfitting',
  'safety',
  'other',
] as const;
export type DrydockTrade = (typeof DRYDOCK_TRADES)[number];

/** Bulgu ve işin saklanan durumu; iş akışı durumları türetilir. */
export const DRYDOCK_RECORD_STATES = ['open', 'cancelled'] as const;
export type DrydockRecordState = (typeof DRYDOCK_RECORD_STATES)[number];

/** Bağın kaynağı; `job` Faz 4'te (iş → iş emri / talep / RA doğrulaması; ADR 0129 Ek Faz 4). */
export const DRYDOCK_LINK_SOURCES = ['checkpoint', 'finding', 'job'] as const;
export type DrydockLinkSource = (typeof DRYDOCK_LINK_SOURCES)[number];
/** Faz 3 hedefleri iş ve arıza; iş emri, talep, operasyonel doğrulama ve sörvey Faz 4'te. */
export const DRYDOCK_LINK_TARGETS = [
  'job',
  'defect',
  'work_order',
  'requisition',
  'ra_verification',
  'survey',
] as const;
export type DrydockLinkTarget = (typeof DRYDOCK_LINK_TARGETS)[number];
/** İlişki (Excel Link "Relation Type"). */
export const DRYDOCK_LINK_RELATIONS = [
  'originates',
  'addresses',
  'verified_by',
  'related',
] as const;
export type DrydockLinkRelation = (typeof DRYDOCK_LINK_RELATIONS)[number];

/**
 * İzinli bağlar (Entity Relationships): nokta → iş (N:M, "Job may originate from multiple
 * checkpoints"), bulgu → iş (N:M; iş bulguyu karşılar), bulgu → arıza (N:M; var olan arıza
 * bağlanır). Nokta arızaya doğrudan bağlanmaz: önce bulgu (R2). Bulgunun noktası bulgu satırındadır
 * (Inspection–Finding 1:N). Faz 4: iş → iş emri (Job–Work Order 1:N; işten açılan `originates`), iş →
 * talep (Job–Purchase Request 1:N), iş → RA doğrulaması (Job–RA/PTW 1:N), bulgu → sörvey
 * (Finding–Survey N:M; sınıf ya da bayrak takibi).
 */
export const DRYDOCK_LINK_RULES: Readonly<
  Record<DrydockLinkSource, Partial<Record<DrydockLinkTarget, readonly DrydockLinkRelation[]>>>
> = {
  checkpoint: { job: ['originates', 'related'] },
  finding: {
    job: ['addresses', 'related'],
    defect: ['originates', 'related'],
    survey: ['related', 'verified_by'],
  },
  job: {
    work_order: ['originates', 'related'],
    requisition: ['related'],
    ra_verification: ['related'],
  },
};

export const DRYDOCK_REVIEW_SUBJECTS = ['finding', 'job'] as const;
export type DrydockReviewSubject = (typeof DRYDOCK_REVIEW_SUBJECTS)[number];

export const DRYDOCK_JOB_DECISIONS = ['approved', 'returned', 'rejected'] as const;
export type DrydockJobDecision = (typeof DRYDOCK_JOB_DECISIONS)[number];

// --- Bulgu ---

export const pdiFindingSchema = z
  .strictObject({
    title: requiredText(200),
    description: requiredParagraph(4000),
    location: requiredText(200),
    /** Excel "Link equipment if possible": isteğe bağlı, aynı gemi. */
    equipmentId: optionalUuid,
    priority: z.enum(PDI_FINDING_PRIORITIES, typed('INVALID_OPTION')),
    safetyCritical: z
      .boolean(typed('INVALID_OPTION'))
      .nullish()
      .transform((value) => value ?? false),
    immediateAction: optionalParagraph(2000),
    disposition: z.enum(PDI_DISPOSITIONS, typed('INVALID_OPTION')),
  })
  .superRefine((value, ctx) => {
    // Excel "Immediate Safety Action — Required if safety-critical"; "Critical escalation"
    if ((value.priority === 'critical' || value.safetyCritical) && !value.immediateAction) {
      ctx.addIssue({
        code: 'custom',
        path: ['immediateAction'],
        message: 'IMMEDIATE_ACTION_REQUIRED',
      });
    }
  });
export type PdiFindingInput = z.output<typeof pdiFindingSchema>;

export function parsePdiFinding(input: unknown): ParseResult<PdiFindingInput> {
  return parsed(pdiFindingSchema.safeParse(input));
}

/** Bulgu numarası: proje + `F` + sıra (en az iki hane; R13 arka uç üretir). */
export function pdiFindingNumber(projectNo: string, seq: number): string {
  return `${projectNo}-F${String(seq).padStart(2, '0')}`;
}

// --- İş ---

const positive = z
  .number(typed('INVALID_NUMBER'))
  .refine((value) => Number.isFinite(value) && value > 0, 'INVALID_NUMBER');

export const drydockJobSchema = z
  .strictObject({
    title: requiredText(200),
    /** Excel "Scope of Work — Detailed task scope and boundaries; Version-controlled". */
    scope: requiredParagraph(8000),
    trade: z
      .enum(DRYDOCK_TRADES, typed('INVALID_OPTION'))
      .nullish()
      .transform((value) => value ?? null),
    contractor: optionalText(200),
    quantity: z
      .union([positive, z.literal('')], typed('INVALID_NUMBER'))
      .nullish()
      .transform((value) => (value === '' || value === undefined ? null : value)),
    unit: optionalText(40),
    plannedStart: optionalDay,
    plannedEnd: optionalDay,
    equipmentId: optionalUuid,
  })
  .superRefine((value, ctx) => {
    if (value.plannedStart && value.plannedEnd && value.plannedEnd < value.plannedStart) {
      ctx.addIssue({ code: 'custom', path: ['plannedEnd'], message: 'END_BEFORE_START' });
    }
    if (value.quantity !== null && !value.unit) {
      ctx.addIssue({ code: 'custom', path: ['unit'], message: 'REQUIRED' });
    }
  });
export type DrydockJobInput = z.output<typeof drydockJobSchema>;

export function parseDrydockJob(input: unknown): ParseResult<DrydockJobInput> {
  return parsed(drydockJobSchema.safeParse(input));
}

/** İş numarası: proje + `J` + sıra (en az üç hane). */
export function drydockJobNumber(projectNo: string, seq: number): string {
  return `${projectNo}-J${String(seq).padStart(3, '0')}`;
}

// --- Bağ ---

export const drydockLinkSchema = z
  .strictObject({
    sourceType: z.enum(DRYDOCK_LINK_SOURCES, typed('INVALID_OPTION')),
    /** Bulgu ya da iş kaynağında kaydın kimliği. */
    sourceId: optionalUuid,
    /** Nokta kaynağında denetim ve kontrol noktası kodu (dondurulmuş sürümden). */
    inspectionId: optionalUuid,
    checkpointCode: z
      .string(typed('INVALID_TEXT'))
      .trim()
      .nullish()
      .transform((value) => (value ? value : null)),
    targetType: z.enum(DRYDOCK_LINK_TARGETS, typed('INVALID_OPTION')),
    targetId: z.uuid(typed('INVALID_ID')),
    relation: z.enum(DRYDOCK_LINK_RELATIONS, typed('INVALID_OPTION')),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (value.sourceType === 'checkpoint') {
      if (!value.inspectionId) issue('inspectionId', 'REQUIRED');
      if (!value.checkpointCode) issue('checkpointCode', 'REQUIRED');
      else if (pdiLevelOf(value.checkpointCode) !== 'checkpoint') {
        issue('checkpointCode', 'PDI_NOT_CHECKPOINT');
      }
      if (value.sourceId) issue('sourceId', 'INVALID_LINK');
    } else {
      if (!value.sourceId) issue('sourceId', 'REQUIRED');
      if (value.inspectionId || value.checkpointCode) issue('checkpointCode', 'INVALID_LINK');
    }
    const relations = DRYDOCK_LINK_RULES[value.sourceType][value.targetType];
    if (!relations) issue('targetType', 'INVALID_LINK');
    else if (!relations.includes(value.relation)) issue('relation', 'INVALID_LINK');
  });
export type DrydockLinkInput = z.output<typeof drydockLinkSchema>;

export function parseDrydockLink(input: unknown): ParseResult<DrydockLinkInput> {
  return parsed(drydockLinkSchema.safeParse(input));
}

// --- Ofis kararı ---

export const drydockJobDecisionSchema = z
  .strictObject({
    decision: z.enum(DRYDOCK_JOB_DECISIONS, typed('INVALID_OPTION')),
    reason: optionalParagraph(2000),
    contentHash: z.string(typed('INVALID_HASH')).regex(/^[0-9a-f]{64}$/, 'INVALID_HASH'),
  })
  .superRefine((value, ctx) => {
    if (value.decision !== 'approved' && !value.reason) {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'REASON_REQUIRED' });
    }
  });
export type DrydockJobDecisionInput = z.output<typeof drydockJobDecisionSchema>;

export function parseDrydockJobDecision(input: unknown): ParseResult<DrydockJobDecisionInput> {
  return parsed(drydockJobDecisionSchema.safeParse(input));
}

// --- Onay kopyası ---

const orNull = <T>(value: T | null | undefined): T | null => value ?? null;

export interface PdiFindingSnapshotInput {
  findingNo: string;
  checkpointCode: string;
  title: string;
  description: string;
  location: string;
  equipmentId?: string | null | undefined;
  priority: string;
  safetyCritical: boolean;
  immediateAction?: string | null | undefined;
  disposition: string;
}

/** Bulgunun inceleme kopyası (hash'lenen içerik). */
export function pdiFindingSnapshot(input: PdiFindingSnapshotInput) {
  return {
    findingNo: input.findingNo,
    checkpointCode: input.checkpointCode,
    title: input.title,
    description: input.description,
    location: input.location,
    equipmentId: orNull(input.equipmentId),
    priority: input.priority,
    safetyCritical: input.safetyCritical === true,
    immediateAction: orNull(input.immediateAction),
    disposition: input.disposition,
  };
}
export type PdiFindingSnapshot = ReturnType<typeof pdiFindingSnapshot>;

export interface DrydockJobSnapshotFields {
  jobNo: string;
  title: string;
  scope: string;
  trade?: string | null | undefined;
  contractor?: string | null | undefined;
  quantity?: number | null | undefined;
  unit?: string | null | undefined;
  plannedStart?: string | null | undefined;
  plannedEnd?: string | null | undefined;
  equipmentId?: string | null | undefined;
  /** Önceki projeden taşınan işin kaynağı (ADR 0129 Ek Faz 4). */
  carriedFromJobId?: string | null | undefined;
}

/** İşin kaynağı: kontrol noktası (kod) ya da bulgu (kimlik). */
export interface DrydockOrigin {
  type: DrydockLinkSource;
  ref: string;
}

/**
 * İşin gönderim kopyası (ofisin onayladığı içerik): alanlar + iptal edilmemiş kaynak bağları
 * (sıralı, tekil). Kaynak değişirse iş yeniden onaya düşer. Taşınan işte kaynağı (`carriedFromJobId`)
 * da kopyadadır; taşınmamış işte anahtar yoktur, Faz 3 hash'leri değişmez.
 */
export function drydockJobSnapshot(fields: DrydockJobSnapshotFields, origins: DrydockOrigin[]) {
  const keys = [...new Set(origins.map((o) => `${o.type}:${o.ref}`))].sort();
  const snapshot = {
    jobNo: fields.jobNo,
    title: fields.title,
    scope: fields.scope,
    trade: orNull(fields.trade),
    contractor: orNull(fields.contractor),
    quantity: orNull(fields.quantity),
    unit: orNull(fields.unit),
    plannedStart: orNull(fields.plannedStart),
    plannedEnd: orNull(fields.plannedEnd),
    equipmentId: orNull(fields.equipmentId),
    origins: keys.map((key) => {
      const at = key.indexOf(':');
      return { type: key.slice(0, at) as DrydockLinkSource, ref: key.slice(at + 1) };
    }),
  };
  return fields.carriedFromJobId
    ? { ...snapshot, carriedFromJobId: fields.carriedFromJobId }
    : snapshot;
}
export type DrydockJobSnapshot = ReturnType<typeof drydockJobSnapshot>;

// --- Türetilmiş durumlar ---

export interface DrydockSubmissionRef {
  id: string;
  contentHash: string;
}
export interface DrydockDecisionRef {
  reviewId: string;
  decision: DrydockJobDecision;
  contentHash: string;
}

export type DrydockJobStatus =
  | 'draft'
  | 'for_review'
  | 'approved'
  | 'returned'
  | 'rejected'
  | 'cancelled';

export interface DrydockJobStatusView {
  status: DrydockJobStatus;
  /** Taslağa düşme nedeni: onaydan ya da gönderimden sonra içerik değişti. */
  changed: 'none' | 'since_submission' | 'since_approval';
  /** Son gönderim (ofis kararı buna verilir). */
  submissionId: string | null;
  decision: DrydockJobDecision | null;
}

/**
 * İş durumu (Excel "Approval State"; ADR 0129 §6): son gönderim ve ona verilen karar; kopya bugünkü
 * içerikle aynı değilse iş taslaktır. Yard Job List yalnız `approved`.
 */
export function drydockJobStatus(input: {
  cancelled: boolean;
  currentHash: string;
  /** Revizyon sırasıyla. */
  submissions: readonly DrydockSubmissionRef[];
  decisions: readonly DrydockDecisionRef[];
}): DrydockJobStatusView {
  const last = input.submissions.at(-1);
  const decision = last ? input.decisions.find((d) => d.reviewId === last.id) : undefined;
  const view = {
    submissionId: last?.id ?? null,
    decision: decision?.decision ?? null,
  };
  if (input.cancelled) return { status: 'cancelled', changed: 'none', ...view };
  if (!last) return { status: 'draft', changed: 'none', ...view };
  if (last.contentHash !== input.currentHash) {
    return {
      status: 'draft',
      changed: decision?.decision === 'approved' ? 'since_approval' : 'since_submission',
      ...view,
    };
  }
  if (!decision) return { status: 'for_review', changed: 'none', ...view };
  return { status: decision.decision, changed: 'none', ...view };
}

/**
 * Gönderim kuralı (R14): iş en az bir kaynağa bağlı; bekleyen ya da onaylı içerik yeniden
 * gönderilmez; iade ya da ret edilen içerik değişmeden yeniden gönderilmez.
 */
export function assertDrydockJobSubmittable(input: {
  currentHash: string;
  origins: number;
  submissions: readonly DrydockSubmissionRef[];
  decisions: readonly DrydockDecisionRef[];
}): void {
  if (input.origins < 1) throw new DomainError('DRYDOCK_JOB_NO_ORIGIN');
  const last = input.submissions.at(-1);
  if (!last || last.contentHash !== input.currentHash) return;
  const decision = input.decisions.find((d) => d.reviewId === last.id);
  if (decision && decision.decision !== 'approved') {
    throw new DomainError('DRYDOCK_JOB_UNCHANGED');
  }
  throw new DomainError('DRYDOCK_ALREADY_REVIEWED');
}

export type PdiFindingStatus =
  | 'draft'
  | 'reviewed'
  | 'actioned'
  | 'closing'
  | 'closed'
  | 'cancelled';

/**
 * Bulgu durumu (Excel Finding "Status"): son teknik incelemenin kopyası bugünkü içerikle aynıysa
 * incelenmiş; incelenmiş ve onaylı işe ya da arızaya bağlıysa `actioned`. Faz 4: iptal edilmemiş
 * kapanış Closed; kritik bulgunun kapanışı Superintendent doğrulayana dek `closing`, iade edilirse
 * bulgu açık kalır (Screen 11 "Critical findings require authorized review").
 */
export function pdiFindingStatus(input: {
  cancelled: boolean;
  currentHash: string;
  reviews: readonly { contentHash: string }[];
  actioned: boolean;
  closure?: { critical: boolean; verdict: 'verified' | 'returned' | null } | null;
}): { status: PdiFindingStatus; changed: boolean } {
  if (input.cancelled) return { status: 'cancelled', changed: false };
  const closure = input.closure;
  if (closure && (!closure.critical || closure.verdict === 'verified')) {
    return { status: 'closed', changed: false };
  }
  if (closure?.critical && closure.verdict === null) return { status: 'closing', changed: false };
  const last = input.reviews.at(-1);
  if (!last) return { status: 'draft', changed: false };
  if (last.contentHash !== input.currentHash) return { status: 'draft', changed: true };
  return { status: input.actioned ? 'actioned' : 'reviewed', changed: false };
}
